import { AccountDetailsModal } from 'components/accounts/AccountDetailsModal'
import { AccountHeaderModal } from 'components/accounts/AccountHeaderModal'
import {
  Button,
  Card,
  CardContent,
  ExclamationCircleIcon,
  Typography,
  createStyles,
  makeStyles,
  useModal,
  useRouter,
} from '@forgeglobal/design-framework'
import {
  DoNotContactReason,
  RelationshipType,
  useAccountContactMethodsQuery,
  useAccountContactPreferencesQuery,
} from 'graphql/bindings/AdminBindings'
import { StyleDefaults } from 'isolated-design-library'
import { doNotContactReasonDisplayName, sortDoNotContactReasons } from 'util/DoNotContactUtils'

import { RelationshipShape, RelationshipsModal } from 'components/shared/RelationshipsModal'
import { childRelationshipMap } from 'util/RelationshipUtils'
import React from 'react'

const useStyles = makeStyles(({ spacing }) =>
  createStyles({
    root: {
      color: StyleDefaults.color.warning,
      marginRight: spacing(3),
      marginLeft: spacing(3),
      marginBottom: spacing(3),
      fontSize: 12,
      fontFamily: 'Roboto',
      fontWeight: 500,
      minWidth: 0,
    },
    viewMore: {
      fontSize: 14,
      cursor: 'pointer',
      textAlign: 'right',
      textTransform: 'uppercase',
      textDecoration: 'none',
      flex: 2,
    },
    reasons: {
      display: 'flex',
      flexDirection: 'column',
    },
    content: {
      display: 'flex',
      alignItems: 'flex-start',
    },
    icon: {
      fontSize: 28,
      marginRight: 8,
    },
  })
)

export const AccountCallout: React.FC = () => {
  const classes = useStyles()
  const { match } = useRouter<{ id: string }>()
  const accountId = match.params.id
  const { data } = useAccountContactPreferencesQuery({ variables: { id: accountId } })
  const account = data?.account
  const doNotContactReasons = sortDoNotContactReasons(account?.do_not_contact_reasons ?? [])
  const contactaMethods = useAccountContactMethodsQuery({ variables: { id: accountId } })
  const intermediaryRelationship = contactaMethods.data?.account.intermediary

  const isChildIntermediary = intermediaryRelationship && intermediaryRelationship.parent_account.id === accountId
  const intermediary = isChildIntermediary
    ? intermediaryRelationship?.child_account
    : intermediaryRelationship?.parent_account
  const intermediaryFormData: RelationshipShape | undefined =
    intermediaryRelationship && intermediary && intermediaryRelationship.parent_to_child_relationship
      ? {
          id: intermediaryRelationship.id,
          account: {
            label: intermediary.name,
            value: {
              id: intermediary.id,
              name: intermediary.name,
              type: intermediary.type,
            },
          },
          is_intermediary: true,
          relationship: (isChildIntermediary
            ? childRelationshipMap[intermediaryRelationship.parent_to_child_relationship]
            : intermediaryRelationship.parent_to_child_relationship) as RelationshipType | undefined,
          title: intermediaryRelationship?.title,
          type: intermediary.type,
        }
      : undefined

  const [editForgeDeclined, hideModal1] = useModal(() => {
    return (
      <AccountHeaderModal
        hideModal={hideModal1}
        accountId={accountId}
        editSection={'forge_declined'}
        highlightDNCFields={true}
      />
    )
  }, [accountId])

  const [editLocation, hideModal2] = useModal(() => {
    return (
      <AccountHeaderModal
        hideModal={hideModal2}
        accountId={accountId}
        editSection={'country'}
        highlightDNCFields={true}
      />
    )
  }, [accountId])

  const [editSensitivity, hideModal3] = useModal(() => {
    return (
      <AccountDetailsModal
        hideModal={hideModal3}
        accountId={accountId}
        editSection={'sensitivity'}
        highlightDNCFields={true}
      />
    )
  }, [accountId])

  const [editIntermediary, hideModal4] = useModal(() => {
    return (
      <RelationshipsModal
        hideModal={hideModal4}
        accountId={accountId}
        accountName={account?.name || ''}
        accountType={account?.type}
        editing
        existingRelationship={intermediaryFormData}
        highlightIntermediarySection={true}
      />
    )
  }, [accountId, intermediary])

  if (doNotContactReasons.length === 0) return null

  const editButton = (dncReason: DoNotContactReason) => {
    let clickHandler: (() => void) | null = null
    switch (dncReason) {
      case DoNotContactReason.IsForgeDeclined:
        clickHandler = editForgeDeclined
        break
      case DoNotContactReason.IsInRestrictedLocation:
        clickHandler = editLocation
        break
      case DoNotContactReason.IsHighSensitivity:
        clickHandler = editSensitivity
        break
      case DoNotContactReason.HasIntermediary:
        clickHandler = editIntermediary
        break
    }
    if (clickHandler) {
      return (
        <Button size="small" variant="text" color="secondary" onClick={clickHandler}>
          EDIT
        </Button>
      )
    }
    return null
  }

  return (
    <Card className={classes.root}>
      <CardContent className={classes.content}>
        <ExclamationCircleIcon className={classes.icon} />
        <div className={classes.reasons}>
          <Typography variant="subtitle1">Do not contact about deals:</Typography>
          {doNotContactReasons.map(reason => (
            <div key={reason}>
              <Typography variant="caption" color="textPrimary">
                {doNotContactReasonDisplayName(reason)}
              </Typography>
              {editButton(reason)}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
